// The header action that re-evaluates markers on one transcript: a Popover
// checklist of the transcript's markers with their current value, none
// checked by default. Uses the caller's rerun mutation; toasts are the
// caller's.

import { useEffect, useRef, useState } from "react";

import type { components } from "../../api/schema";
import { firstErrorMessage } from "../../lib/errors";
import type { ListQueryResult } from "../../lib/useListQuery";
import {
  Button,
  Checkbox,
  Inline,
  InlineError,
  Popover,
  Section,
  Stack,
  Text,
} from "../../ui";
import {
  rerunMarkerOptions,
  type RerunMarkerOption,
  type ResultsRailRow,
} from "./resultsRailRows";
import type { useTranscriptRerun } from "./useTranscriptRerun";

type MarkerResponse = components["schemas"]["MarkerResponse"];

// The header action as the canvas container wires it: the transcript's rows
// and the agent's marker catalog feed the checklist, the rerun mutation the
// submit.
export function TranscriptRerunAction({
  markers,
  rerun,
  rows,
}: {
  markers: ListQueryResult<MarkerResponse>;
  rerun: ReturnType<typeof useTranscriptRerun>;
  rows: ResultsRailRow[];
}) {
  return (
    <TranscriptRerunControl
      errorMessage={firstErrorMessage(rerun.error)}
      hasMoreMarkers={markers.hasNextPage}
      isLoadingMarkers={markers.isFetchingNextPage}
      isPending={rerun.isPending}
      markers={rerunMarkerOptions(rows, markers.items)}
      onLoadMoreMarkers={markers.fetchNextPage}
      onSubmit={(markerIds) => {
        rerun.mutate(markerIds);
      }}
    />
  );
}

export interface TranscriptRerunControlProps {
  errorMessage?: string | null;
  hasMoreMarkers: boolean;
  isLoadingMarkers: boolean;
  isPending: boolean;
  markers: RerunMarkerOption[];
  onLoadMoreMarkers: () => void;
  onSubmit: (markerIds: string[]) => void;
}

export function TranscriptRerunControl({
  errorMessage,
  hasMoreMarkers,
  isLoadingMarkers,
  isPending,
  markers,
  onLoadMoreMarkers,
  onSubmit,
}: TranscriptRerunControlProps) {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen && hasMoreMarkers && !isLoadingMarkers) {
      onLoadMoreMarkers();
    }
  }, [hasMoreMarkers, isLoadingMarkers, isOpen, onLoadMoreMarkers]);

  const loading = isLoadingMarkers || hasMoreMarkers;
  const allSelected =
    markers.length > 0 &&
    markers.every((marker) => selected.includes(marker.id));

  return (
    <>
      <Button
        ref={anchorRef}
        size="sm"
        variant="secondary"
        onClick={() => {
          setIsOpen((open) => !open);
        }}
      >
        Re-evaluate…
      </Button>
      <Popover
        align="end"
        anchorRef={anchorRef}
        aria-label="Re-evaluate markers on this transcript"
        isOpen={isOpen}
        size="wide"
        onDismiss={() => {
          setIsOpen(false);
        }}
      >
        <Section
          actions={
            markers.length > 0 ? (
              <Button
                size="sm"
                type="button"
                variant="ghost"
                onClick={() => {
                  setSelected(
                    allSelected ? [] : markers.map((marker) => marker.id),
                  );
                }}
              >
                {allSelected ? "Clear all" : "Select all"}
              </Button>
            ) : null
          }
          heading="Re-evaluate markers on this transcript"
        >
          <Stack gap={3}>
            {markers.length === 0 && !loading ? (
              <Text variant="muted">
                No markers are available for this agent.
              </Text>
            ) : null}
            <Stack className="marker-rerun__list" gap={2}>
              {markers.map((marker) => (
                <div className="marker-rerun__row" key={marker.id}>
                  <Checkbox
                    checked={selected.includes(marker.id)}
                    label={marker.name}
                    onChange={(event) => {
                      const checked = event.currentTarget.checked;
                      setSelected((current) =>
                        checked
                          ? [...current, marker.id]
                          : current.filter((id) => id !== marker.id),
                      );
                    }}
                  />
                  <Text
                    as="span"
                    className="marker-rerun__value"
                    variant="small"
                  >
                    {marker.valueText}
                  </Text>
                </div>
              ))}
            </Stack>
            <Text variant="muted">
              Runs the selected markers again on this transcript. Existing
              answers stay in history; new Marks appear in the rail as they
              land.
            </Text>
            <Inline gap={2}>
              <Button
                disabled={isPending || loading || selected.length === 0}
                size="sm"
                onClick={() => {
                  onSubmit(selected);
                }}
              >
                {isPending
                  ? "Queueing..."
                  : loading
                    ? "Loading markers..."
                    : `Re-evaluate ${String(selected.length)} ${
                        selected.length === 1 ? "marker" : "markers"
                      }`}
              </Button>
              <Button
                size="sm"
                type="button"
                variant="secondary"
                onClick={() => {
                  setIsOpen(false);
                }}
              >
                Cancel
              </Button>
            </Inline>
            {errorMessage ? <InlineError>{errorMessage}</InlineError> : null}
          </Stack>
        </Section>
      </Popover>
    </>
  );
}
